import { useEffect } from 'react';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useExperienceAbort } from '@atlassian/jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useRankNavigationItem } from '@atlassian/jira-navigation-kit-rank-item-service-old/src/services/index.tsx';

// exported for testing
export const PACKAGE_NAME = 'jiraNavigationKitRankItemOld';
export const REORDER_VIEW_EXPERIENCE = { experience: 'reorderView' };

const BEFORE = 'before';
const AFTER = 'after';
type Placement = typeof BEFORE | typeof AFTER;

/** node.id: this is the id being returned by the backend in the navigation items to identify the nav item
 * __id: identifier relay automatically adds to store GraphQL records
 */
type TabNode = {
	readonly id: string;
	__id?: string;
};
type TabEdge = {
	readonly node: TabNode;
};

export const useDroppableTabListOld = <TabNodeType extends TabEdge>(
	tabs: TabNodeType[],
): { tabs: TabNodeType[] } => {
	const onStartExperience = useExperienceStart({
		...REORDER_VIEW_EXPERIENCE,
		analyticsSource: PACKAGE_NAME,
	});
	const onExperienceAbort = useExperienceAbort(REORDER_VIEW_EXPERIENCE);
	const [rankNavigationItem] = useRankNavigationItem();

	useEffect(
		() =>
			monitorForElements({
				canMonitor({ source }) {
					return source.data.type === 'tab';
				},
				onDrop({ source, location }) {
					onStartExperience();
					const destination = location.current.dropTargets[0];
					if (!destination) {
						onExperienceAbort('Dropped outside of drop targets');
						return;
					}

					if (source.data.type === 'tab' && source.data.id !== destination.data.id) {
						const sourceTabIndex = tabs.findIndex((tab: TabEdge) => tab.node.id === source.data.id);

						const targetTabIndex = tabs.findIndex(
							(tab: TabEdge) => tab.node.id === destination.data.id,
						);
						const closestEdgeOfTarget = extractClosestEdge(destination.data);
						const reorderedTabs = reorderWithEdge({
							list: tabs,
							startIndex: sourceTabIndex,
							indexOfTarget: targetTabIndex,
							closestEdgeOfTarget,
							axis: 'horizontal',
						});

						if (reorderedTabs && tabs.every((value, index) => value === reorderedTabs[index]))
							return;

						const placement: Placement = closestEdgeOfTarget === 'left' ? BEFORE : AFTER;

						const { id, __id: dataId } = tabs[sourceTabIndex].node;
						const beforeItemId = placement === BEFORE ? tabs[targetTabIndex].node.id : null;
						const afterItemId = placement === AFTER ? tabs[targetTabIndex].node.id : null;

						if (
							id.includes('optimisticId') ||
							(beforeItemId && beforeItemId.includes('optimisticId')) ||
							(afterItemId && afterItemId.includes('optimisticId'))
						)
							return;

						rankNavigationItem({
							placement,
							sourceTabIndex,
							targetTabIndex,
							id,
							beforeItemId,
							afterItemId,
							dataId,
							actionType: 'dragged',
						});
					}
				},
			}),
		// PLEASE CONFIRM THESE DEPENDENCIES ARE CORRECT

		[tabs, rankNavigationItem, onStartExperience, onExperienceAbort],
	);

	return { tabs };
};
